"use client";

import React, { useEffect } from "react";
import { Menu, Phone, Mail, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-6">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-4 w-16 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-[rgb(79,94,156)] hover:bg-transparent hover:text-[#576192] focus:bg-transparent focus:text-[#576192] text-lg font-medium transition-colors duration-300")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-4 mt-8">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-xl font-medium text-[rgb(79,94,156)] hover:text-[#576192] transition-colors py-2 border-b border-gray-100">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function HeaderPrincipale() {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className="fixed top-0 left-0 right-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-[#eeeeee] py-4 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
        {}
        <Link to="/" className="flex-shrink-0">
          <img src="https://active-phoenix.10web.cloud/wp-content/uploads/2026/03/lora_serramenti_cirie_torino.webp" alt="Lora Serramenti" className="object-contain !w-[48px] !h-[75px]" />
        </Link>

        {}
        <div className="hidden lg:block">
          <MenuProvider menu_id="28">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-3 sm:gap-4">
          {}
          <Button asChild variant="ghost" size="icon" className="rounded-full text-gray-500 hover:text-primary transition-colors">
            <Link to="tel:+393349255303">
              <Phone className="size-5" />
            </Link>
          </Button>

          <Button asChild variant="ghost" size="icon" className="rounded-full text-gray-500 hover:text-primary transition-colors">
            <Link to="mailto:info@loraserramenti.it">
              <Mail className="size-5" />
            </Link>
          </Button>

          {}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-[#333333] hover:bg-gray-100">
                  <Menu className="size-6" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-white">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <img src="https://active-phoenix.10web.cloud/wp-content/uploads/2026/03/image_63lidpz7.png" alt="Lora Serramenti" className="w-auto !h-[70px]" />
                  </SheetTitle>
                </SheetHeader>
                <MenuProvider menu_id="28">
                  <MobileMenuConsumer />
                </MenuProvider>
                <div className="mt-8 pt-8 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-4">Contattaci direttamente</p>
                  <Button asChild className="w-full rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold h-12">
                    <Link to="https://wa.me/393349255303" newTab>
                      <Phone className="mr-2 size-5 fill-current" />
                      Chatta su WhatsApp
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}